import React, { useState, useEffect } from "react";
import {
  Box,
  Flex,
  Text,
  IconButton,
  Stack,
  Collapse,
  useDisclosure,
  useColorMode,
  useColorModeValue,
  Link,
  Container,
} from "@chakra-ui/react";
import { Menu, X, Sun, Moon } from "lucide-react";
import { motion } from "framer-motion";

const MotionBox = motion(Box);

const Navbar: React.FC = () => {
  const { isOpen, onToggle } = useDisclosure();
  const { colorMode, toggleColorMode } = useColorMode();
  const [scrolled, setScrolled] = useState(false);

  const bg = useColorModeValue(
    scrolled ? "white" : "transparent",
    scrolled ? "gray.800" : "transparent"
  );
  const logoColor = useColorModeValue(
    scrolled ? "brand.500" : "white",
    scrolled ? "brand.300" : "white"
  );
  const linkColor = useColorModeValue(
    scrolled ? "gray.600" : "white",
    scrolled ? "gray.300" : "white"
  );
  const linkHoverColor = useColorModeValue(
    scrolled ? "brand.500" : "brand.200",
    scrolled ? "brand.300" : "brand.200"
  );
  const mobileBg = useColorModeValue("white", "gray.800");
  const mobileLinkColor = useColorModeValue("gray.600", "gray.300");
  const mobileLinkHoverColor = useColorModeValue("brand.500", "brand.300");

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navItems = [
    { label: "About", href: "#about" },
    { label: "Skills", href: "#skills" },
    { label: "Projects", href: "#projects" },
    { label: "Experience", href: "#experience" },
    { label: "Contact", href: "#contact" },
  ];

  return (
    <MotionBox
      position="fixed"
      top={0}
      w="full"
      zIndex={1000}
      bg={bg}
      backdropFilter={scrolled ? "blur(10px)" : "none"}
      boxShadow={scrolled ? "lg" : "none"}
      transition="all 0.3s ease"
      initial={{ y: -100 }}
      animate={{ y: 0 }}
    >
      <Container maxW="7xl">
        <Flex
          minH={"60px"}
          py={2}
          alignItems="center"
          justifyContent="space-between"
        >
          <Flex display={{ base: "flex", md: "none" }}>
            <IconButton
              onClick={onToggle}
              icon={isOpen ? <X size={16} /> : <Menu size={16} />}
              variant={"ghost"}
              aria-label={"Toggle Navigation"}
              color={linkColor}
            />
          </Flex>

          <Flex
            flex={{ base: 1 }}
            align="center"
            justify={{ base: "center", md: "space-between" }}
          >
            <Link href="#hero" _hover={{ textDecoration: "none" }}>
              <Text
                textAlign={{ base: "center", md: "left" }}
                fontFamily={"heading"}
                fontSize="xl"
                fontWeight="bold"
                color={logoColor}
                transition="color 0.3s ease"
              >
                Jelili Abdullahi A.
              </Text>
            </Link>

            <Flex display={{ base: "none", md: "flex" }} ml={10}>
              <Stack direction={"row"} spacing={4}>
                {navItems.map((navItem) => (
                  <Link
                    key={navItem.label}
                    p={2}
                    href={navItem.href}
                    fontSize={"sm"}
                    fontWeight={500}
                    color={linkColor}
                    _hover={{
                      textDecoration: "none",
                      color: linkHoverColor,
                    }}
                    transition="color 0.2s ease"
                  >
                    {navItem.label}
                  </Link>
                ))}
              </Stack>
            </Flex>
          </Flex>

          <Flex align="center">
            <IconButton
              aria-label="Toggle color mode"
              icon={
                colorMode === "light" ? <Moon size={16} /> : <Sun size={16} />
              }
              onClick={toggleColorMode}
              variant="ghost"
              color={linkColor}
              _hover={{
                color: linkHoverColor,
                transform: "scale(1.1)",
              }}
              transition="all 0.2s ease"
            />
          </Flex>
        </Flex>

        <Collapse in={isOpen} animateOpacity>
          <Flex
            flexDir="column"
            gap={4}
            bg={mobileBg}
            p={4}
            display={{ md: "none" }}
            borderRadius="md"
            boxShadow="lg"
            pb={20}
            mt={2}
          >
            {navItems.map((navItem) => (
              <Link
                key={navItem.label}
                display="block"
                py={2}
                href={navItem.href}
                fontWeight={600}
                color={mobileLinkColor}
                _hover={{
                  textDecoration: "none",
                  color: mobileLinkHoverColor,
                }}
                onClick={onToggle}
              >
                {navItem.label}
              </Link>
            ))}
          </Flex>
        </Collapse>
      </Container>
    </MotionBox>
  );
};

export default Navbar;
